/**
 * Загрузка документов о долгах в таблицу кредиторов (решения от 25.09.2026):
 * кредитные отчёты «Скоринг бюро» и ОКБ, постановления ФССП о возбуждении
 * исполнительного производства. Можно выбрать несколько файлов сразу.
 *
 * Сервер разбирает PDF и возвращает ПРЕДЛОЖЕНИЕ строк. В дело попадает
 * только то, что сотрудник отметил: какие долги включать, решает юрист.
 *
 * ДВОЙНОЙ УЧЁТ. Один и тот же долг бывает и в кредитном отчёте,
 * и в постановлении ФССП — если кредитор взыскивал его через суд. Дважды
 * включённый долг в заявлении в суд — ошибка по существу. Поэтому строка,
 * кредитор которой уже есть в деле или в другом загруженном документе,
 * приходит БЕЗ отметки и с пояснением: включить её — осознанное решение.
 *
 * Для сверки показывается и то, на кого документ: если ИНН не совпадает
 * с должником дела, это видно сразу.
 */

import { useState } from 'react';
import { creditorCoreKey, type Creditor } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api, type CreditorsImport } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';

type Row = CreditorsImport['rows'][number];

const DUPLICATE_NOTE = 'У этого кредитора уже есть строка — проверьте, не тот же ли это долг';

/** Строки, чей кредитор уже встречается в деле или в другом документе. */
function possibleDuplicates(results: CreditorsImport[], creditors: Creditor[]): Set<string> {
  const inCase = new Set(creditors.map((c) => creditorCoreKey(c.name)).filter(Boolean));
  const byDocument = new Map<string, Set<number>>();
  results.forEach((result, r) =>
    result.rows.forEach((row) => {
      const key = creditorCoreKey(row.creditor.name);
      if (!byDocument.has(key)) byDocument.set(key, new Set());
      byDocument.get(key)!.add(r);
    }),
  );

  const flagged = new Set<string>();
  results.forEach((result, r) =>
    result.rows.forEach((row, i) => {
      const key = creditorCoreKey(row.creditor.name);
      if (!key) return;
      // Два кредита одного банка в одном отчёте — обычное дело, не повтор
      if (inCase.has(key) || (byDocument.get(key)?.size ?? 0) > 1) flagged.add(`${r}:${i}`);
    }),
  );
  return flagged;
}

export function CreditorDocumentsImport({
  caseId,
  creditors,
  debtorInn,
  readOnly,
  onAccept,
}: {
  caseId: string;
  creditors: Creditor[];
  debtorInn: string;
  readOnly: boolean;
  onAccept: (rows: Creditor[]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<CreditorsImport[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [duplicates, setDuplicates] = useState<Set<string>>(new Set());
  const [accepted, setAccepted] = useState<string | null>(null);

  const load = async (files: File[]) => {
    setBusy(true);
    setErrors([]);
    setResults([]);
    setAccepted(null);
    const uids = creditors.map((c) => c.sourceUid).filter((uid): uid is string => Boolean(uid));
    const loaded: CreditorsImport[] = [];
    const failed: string[] = [];

    // По одному: сервер разбирает отчёт в 250 листов несколько секунд,
    // и десяток файлов разом ему незачем
    for (const file of files) {
      try {
        loaded.push(await api.importCreditorsDocument(caseId, file, uids));
      } catch (e) {
        failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }

    const flagged = possibleDuplicates(loaded, creditors);
    const all = loaded.flatMap((result, r) => result.rows.map((_, i) => `${r}:${i}`));
    setResults(loaded);
    setDuplicates(flagged);
    setSelected(new Set(all.filter((key) => !flagged.has(key))));
    setErrors(failed);
    setBusy(false);
  };

  const toggle = (key: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Все строки документа разом — для галочки в заголовке таблицы */
  const toggleDocument = (r: number, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      results[r]!.rows.forEach((_, i) => (on ? next.add(`${r}:${i}`) : next.delete(`${r}:${i}`)));
      return next;
    });

  const chosen = results.flatMap((result, r) => result.rows.filter((_, i) => selected.has(`${r}:${i}`)).map((row) => row.creditor));

  const accept = () => {
    onAccept(chosen);
    const withoutAddress = chosen.filter((c) => !c.address).length;
    const withoutType = chosen.filter((c) => !c.obligationType).length;
    setAccepted(
      [
        `Добавлено строк: ${chosen.length}.`,
        withoutAddress ? `У ${withoutAddress} нет адреса кредитора — впишите его; после сборки документов адрес сохранится в справочник.` : '',
        withoutType ? `У ${withoutType} не указано содержание обязательства — заполните по постановлению.` : '',
      ]
        .filter(Boolean)
        .join(' '),
    );
    setResults([]);
  };

  const debtor = debtorInn.replace(/\s/g, '');
  const total = chosen.reduce((sum, c) => sum + c.amountDebt, 0);

  return (
    <div className="report-import">
      <DocumentUpload
        label="Загрузить документы о долгах"
        busyLabel="Читаю документы…"
        hint="Кредитный отчёт «Скоринг бюро» или ОКБ, постановления ФССП. Можно несколько файлов сразу. Файлы нигде не сохраняются."
        busy={busy}
        disabled={readOnly}
        onFiles={(files) => void load(files)}
      />

      {errors.map((error, i) => (
        <p key={i} className="error-text">
          {error}
        </p>
      ))}
      {accepted && <p className="hint">{accepted}</p>}

      {results.length > 0 && (
        <div className="draft">
          {results.map((result, r) => (
            <div key={r} className="import-document">
              <p>
                <strong>{result.title}</strong>
                {result.subject && (
                  <>
                    {' '}— {result.subject.fullName}
                    {result.subject.inn && `, ИНН ${result.subject.inn}`}
                  </>
                )}
              </p>

              {result.subject?.inn && debtor && result.subject.inn !== debtor && (
                <p className="error-text">ИНН в документе не совпадает с ИНН должника в деле. Проверьте, тот ли документ загружен.</p>
              )}
              {result.totalsMatch === false && (
                <p className="warning">
                  Сумма найденных строк не сходится со строкой «Итого» отчёта — часть договоров могла не распознаться.
                  Сверьте таблицу с отчётом.
                </p>
              )}

              {result.rows.length === 0 && result.skipped.length === 0 ? (
                // Ни строк, ни пропущенных — разбор не нашёл в документе ни одного договора.
                // «Новых долгов нет» здесь было бы неправдой: скорее всего, не справился разбор
                <p className="warning">
                  В документе не найдено ни одного договора. Если в отчёте они есть, разбор с ним не справился: внесите долги
                  вручную и передайте файл разработчику.
                </p>
              ) : result.rows.length === 0 ? (
                <p className="hint">Новых долгов в документе нет: договоры закрыты или уже внесены в дело (см. ниже).</p>
              ) : (
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={result.rows.every((_, i) => selected.has(`${r}:${i}`))}
                          onChange={(e) => toggleDocument(r, e.target.checked)}
                          aria-label="Отметить все строки документа"
                          title="Отметить все / снять все"
                        />
                      </th>
                      <th>Кредитор</th>
                      <th>Содержание</th>
                      <th>Задолженность, ₽</th>
                      <th>Просрочка</th>
                      <th>Адрес</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row: Row, i) => {
                      const key = `${r}:${i}`;
                      const notes = [...(duplicates.has(key) ? [DUPLICATE_NOTE] : []), ...(row.notes ?? [])];
                      return (
                        <tr key={row.creditor.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(key)}
                              onChange={() => toggle(key)}
                              aria-label={`Добавить ${row.creditor.name}`}
                            />
                          </td>
                          <td>
                            {row.creditor.name}
                            {row.creditor.name !== row.reportName && <div className="hint">в документе: {row.reportName}</div>}
                            {notes.map((note) => (
                              <div key={note} className="warning">
                                {note}
                              </div>
                            ))}
                          </td>
                          <td>
                            {row.creditor.obligationType || '—'}
                            <div className="hint" title={row.reportKind}>
                              {row.reportKind.length > 90 ? `${row.reportKind.slice(0, 90)}…` : row.reportKind}
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>{money(row.creditor.amountDebt).digitsWithKopecks}</td>
                          <td>
                            {row.overdueDays ? `${row.overdueDays} дн.` : '—'}
                            {row.creditor.overdueOverThreeMonths && <div className="hint">более 3 мес.</div>}
                          </td>
                          <td>
                            {row.matched ? (
                              <span className="saved">из справочника</span>
                            ) : row.creditor.address ? (
                              <span className="saved">из документа</span>
                            ) : (
                              <span className="warning">вписать</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Долг обнулился после просрочки — на виду: возможно, продан коллектору */}
              {result.skipped
                .filter((s) => s.warning)
                .map((s, i) => (
                  <p key={i} className="warning">
                    {s.reportName}: {s.reason}
                  </p>
                ))}
              {result.skipped.some((s) => !s.warning) && (
                <details>
                  <summary className="hint">Не предлагается: {result.skipped.filter((s) => !s.warning).length}</summary>
                  <ul className="hint">
                    {result.skipped
                      .filter((s) => !s.warning)
                      .map((s, i) => (
                        <li key={i}>
                          {s.reportName} — {s.reason}
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </div>
          ))}

          <div className="toolbar">
            <button type="button" className="primary" disabled={chosen.length === 0} onClick={accept}>
              Добавить отмеченные ({chosen.length}) на {money(total).digitsWithKopecks} ₽
            </button>
            <button type="button" className="link-button" onClick={() => setResults([])}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
